import React from "react";
import ReactApexChart from "react-apexcharts";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

import DataStatisticsTable from "components/Table/DataStatisticsTable";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "components/ui/card";

interface FunnelStageData {
  title: string;
  value: number;
  label: string;
  conversionRate?: number;
}

interface CampaignData {
  name: string;
  contacted: number;
  appointments: number;
  conversion: number;
}

interface OutreachFunnelCardProps {
  funnelData: FunnelStageData[];
  campaignData: CampaignData[];
  title?: string;
}

const OutreachFunnelCard: React.FC<OutreachFunnelCardProps> = ({
  funnelData,
  campaignData,
  title = "Outreach Conversion Funnel",
}) => {
  const tableHeaders = [
    { key: "name", label: "Campaign" },
    { key: "contacted", label: "Contacted" },
    { key: "appointments", label: "Appointments" },
    { key: "conversion", label: "Conversion" },
  ];

  const tableData = campaignData.map((c) => ({
    name: c.name,
    contacted: c.contacted.toLocaleString(),
    appointments: c.appointments.toLocaleString(),
    conversion: `${c.conversion}%`,
  }));

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border/60">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Funnel stages */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          {funnelData.map((stage, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.08 }}
              className="relative rounded-xl border border-border/60 bg-gradient-to-b from-card to-muted/30 p-4 text-center"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {stage.label}
              </p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {stage.value.toLocaleString()}
              </p>
              {stage.conversionRate !== undefined &&
                stage.conversionRate >= 0 &&
                index !== funnelData.length - 1 && (
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary">
                    <ArrowRight className="h-3 w-3" />
                    {stage.conversionRate}% conversion
                  </div>
                )}
            </motion.div>
          ))}
        </div>

        {/* Bar chart */}
        <div className="mb-2 rounded-xl border border-border/60 bg-card p-2 sm:p-4">
          <ReactApexChart
            options={{
              chart: {
                type: "bar",
                height: window.innerWidth < 640 ? 200 : 300,
                toolbar: { show: false },
                foreColor: "hsl(var(--muted-foreground))",
              },
              plotOptions: {
                bar: {
                  horizontal: true,
                  barHeight: "55%",
                  borderRadius: 6,
                  distributed: false,
                },
              },
              colors: ["#6366f1"],
              dataLabels: {
                enabled: true,
                style: {
                  colors: ["#ffffff"],
                  fontSize: window.innerWidth < 640 ? "10px" : "12px",
                  fontWeight: "bold",
                },
                formatter: (val: any) => val.toLocaleString(),
              },
              xaxis: {
                categories: funnelData.map((s) => s.title),
                labels: {
                  style: {
                    fontSize: window.innerWidth < 640 ? "10px" : "12px",
                  },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
              },
              yaxis: {
                labels: {
                  style: {
                    fontSize: window.innerWidth < 640 ? "10px" : "12px",
                  },
                },
              },
              grid: {
                show: true,
                borderColor: "hsl(var(--border))",
                strokeDashArray: 3,
              },
              tooltip: {
                y: { formatter: (val: any) => val.toLocaleString() },
              },
              legend: { show: false },
            }}
            series={[
              {
                name: "Count",
                data: funnelData.map((s) => s.value),
              },
            ]}
            type="bar"
            height={window.innerWidth < 640 ? 200 : 300}
          />
        </div>

        {tableData.length > 0 && (
          <div className="mt-6">
            <h4 className="mb-3 text-sm font-semibold sm:text-base">
              Campaign Conversion Performance
            </h4>
            <div className="overflow-hidden rounded-xl border border-border/60">
              <DataStatisticsTable
                headers={tableHeaders}
                data={tableData}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OutreachFunnelCard;
